import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingBag, Search, Globe, ChevronDown } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCatalog } from '../context/CatalogContext';
import { useTranslation } from '../hooks/useTranslation';
import { LANGUAGES } from '../context/LanguageContext';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { count } = useCart();
  const { products } = useCatalog();
  const { t, lang, setLang } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const searchPanelRef = useRef(null);
  const langRef = useRef(null);
  const searchInputRef = useRef(null);

  const LINKS = [
    { to: '/', label: t('nav.accueil') },
    { to: '/collections', label: t('nav.collections') },
    { to: '/montres', label: t('nav.montres') },
    { to: '/notre-histoire', label: t('nav.histoire') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/suivi', label: t('nav.suivi') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const results = query.trim()
    ? products.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 6)
    : [];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSearchOpen(false); setLangOpen(false); setQuery(''); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [searchOpen]);

  useEffect(() => {
    function onClickOutside(e) {
      const insideSearch = (searchRef.current && searchRef.current.contains(e.target)) || (searchPanelRef.current && searchPanelRef.current.contains(e.target));
      if (!insideSearch) setSearchOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') { setSearchOpen(false); setLangOpen(false); }
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  const goToProduct = (slug) => {
    setSearchOpen(false);
    setQuery('');
    navigate(`/produit/${slug}`);
  };

  return (
    <>
      <header className={`sticky top-0 z-40 transition-all duration-500 ${scrolled ? 'bg-ivory/95 backdrop-blur-md shadow-sm' : 'bg-ivory'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 grid grid-cols-3 items-center h-[68px] lg:h-[80px]">
          {/* Left: mobile menu / desktop nav left */}
          <div className="flex items-center">
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden p-2 -ml-2"
              aria-label={t('nav.ouvrirMenu')}
              data-testid="navbar-mobile-menu-button"
            >
              <Menu className="w-6 h-6" strokeWidth={1.4} />
            </button>
            <nav className="hidden lg:flex items-center gap-8">
              {LINKS.slice(0, 4).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`navbar-link-${l.to.replace('/', '') || 'accueil'}`}
                  className={`text-[11px] tracking-luxury uppercase link-underline transition-colors ${
                    location.pathname === l.to ? 'text-black' : 'text-[#1A1A1A] hover:text-black'
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Center: Logo — shifted slightly left to avoid crowding the right-side icons, size/font untouched */}
          <div className="flex justify-center">
            <Link to="/" className="flex flex-col items-center leading-none whitespace-nowrap -translate-x-10 sm:-translate-x-6 lg:-translate-x-2" data-testid="navbar-logo-link">
              <span className="font-serif text-[20px] sm:text-[26px] lg:text-[34px] tracking-[0.18em] text-black font-medium">MSK WATCH</span>
            </Link>
          </div>

          {/* Right: nav right + search + language + cart */}
          <div className="flex items-center justify-end gap-1 sm:gap-3 md:gap-5">
            <nav className="hidden lg:flex items-center gap-8 mr-2">
              {LINKS.slice(4).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`navbar-link-${l.to.replace('/', '')}`}
                  className={`text-[11px] tracking-luxury uppercase link-underline transition-colors ${
                    location.pathname === l.to ? 'text-black' : 'text-[#1A1A1A] hover:text-black'
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {/* Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setSearchOpen((v) => !v)}
                className="p-1.5 sm:p-2 hover:text-gold transition-colors"
                aria-label={t('nav.rechercher')}
                data-testid="navbar-search-button"
              >
                <Search className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={1.4} />
              </button>
            </div>

            {/* Language selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1 sm:gap-1.5 p-1.5 sm:p-2 hover:text-gold transition-colors"
                aria-label={t('nav.langue')}
                data-testid="navbar-language-button"
              >
                <Globe className="w-[15px] h-[15px] sm:w-4 sm:h-4" strokeWidth={1.4} />
                <span className="text-[9px] sm:text-[10px] tracking-luxury">{currentLang.short}</span>
                <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${langOpen ? 'rotate-180' : ''}`} strokeWidth={1.4} />
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-white shadow-xl border border-[#EAE4D8] py-2 z-50" data-testid="navbar-language-dropdown">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`navbar-language-option-${l.code}`}
                      className={`w-full text-left px-4 py-2.5 text-[12px] tracking-wide transition-colors ${
                        lang === l.code ? 'text-black font-medium bg-cream' : 'text-[#666] hover:bg-cream hover:text-black'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link to="/panier" className="relative p-1.5 sm:p-2 hover:text-black transition-colors" aria-label={t('nav.panier')} data-testid="navbar-cart-link">
              <ShoppingBag className="w-[18px] h-[18px] sm:w-5 sm:h-5" strokeWidth={1.4} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-black text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="navbar-cart-count">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Search panel */}
        {searchOpen && (
          <div ref={searchPanelRef} className="absolute inset-x-0 top-full bg-white shadow-2xl border-t border-[#EAE4D8] animate-fade-up" data-testid="navbar-search-panel">
            <div className="max-w-2xl mx-auto px-4 py-6">
              <div className="relative">
                <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" strokeWidth={1.4} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('search.placeholder')}
                  className="w-full bg-transparent border-b border-[#1A1A1A] pl-7 pb-3 text-base focus:outline-none placeholder:text-[#999]"
                  data-testid="navbar-search-input"
                />
              </div>
              {query.trim() && (
                <div className="mt-5" data-testid="navbar-search-results">
                  {results.length === 0 ? (
                    <div className="text-sm text-[#888] py-4" data-testid="navbar-search-no-results">{t('search.noResults')}</div>
                  ) : (
                    <div className="divide-y divide-[#EAE4D8]">
                      {results.map((p) => (
                        <button
                          key={p.slug}
                          onClick={() => goToProduct(p.slug)}
                          data-testid={`navbar-search-result-${p.slug}`}
                          className="w-full flex items-center gap-4 py-3 text-left hover:bg-cream transition-colors"
                        >
                          <div className="w-14 h-14 bg-white border border-[#EAE4D8] flex items-center justify-center flex-shrink-0">
                            <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain p-1.5" />
                          </div>
                          <div className="flex-1">
                            <div className="font-serif text-base">{p.name}</div>
                            <div className="text-xs text-[#888] mt-0.5">{p.price} €</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-ivory shadow-2xl animate-slide-in-right flex flex-col">
            <div className="flex items-center justify-between px-6 h-24 border-b border-[#EAE4D8]">
              <span className="font-serif text-2xl tracking-[0.18em] text-black font-medium">MSK WATCH</span>
              <button onClick={() => setOpen(false)} aria-label={t('nav.fermer')} className="p-2 -mr-2" data-testid="navbar-mobile-menu-close"><X className="w-6 h-6" /></button>
            </div>
            <nav className="flex-1 px-6 py-8 flex flex-col gap-6 overflow-y-auto">
              {LINKS.filter((l) => l.to !== '/faq').map((l, i) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`navbar-mobile-link-${l.to.replace('/', '') || 'accueil'}`}
                  className="font-serif text-2xl tracking-wide hover:text-black transition-colors"
                  style={{ animation: `fade-up 0.6s ease-out ${i * 0.05}s both` }}
                >
                  {l.label}
                </Link>
              ))}
              <div className="pt-4 border-t border-[#EAE4D8]">
                <div className="text-[10px] tracking-luxury uppercase text-[#888] mb-3">{t('nav.langue')}</div>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => setLang(l.code)}
                      data-testid={`navbar-mobile-language-option-${l.code}`}
                      className={`px-3 py-1.5 text-[11px] tracking-luxury uppercase border transition-colors ${
                        lang === l.code ? 'border-black bg-black text-white' : 'border-[#EAE4D8] text-[#666] hover:border-black hover:text-black'
                      }`}
                    >
                      {l.short}
                    </button>
                  ))}
                </div>
              </div>
            </nav>
            <div className="px-6 py-6 border-t border-[#EAE4D8] text-[11px] tracking-luxury uppercase text-[#666]">
              {t('nav.assembledIn')}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
